"use client";
import React, { useState } from "react";
import {
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Box, IconButton, Typography, Stack, Tooltip, Chip
} from "@mui/material";
import { useQuery } from "react-query";
import { getCapacityAssessmentByReason } from "../../../../../../../api/services/Assessment/ConsentCapacity";
import { QK } from "../../../../../../../base/qk";
import { Delete, Edit, Verified } from "@mui/icons-material";
import ConsentDialog from "../ConsentDialog";

const AssessmentTable = ({ consentReasonId, onDelete }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConsent, setEditingConsent] = useState(null);

  const { data, isLoading } = useQuery(
    [QK.capacityAssessmentByReason, consentReasonId],
    () => getCapacityAssessmentByReason(consentReasonId),
    { 
        enabled: !!consentReasonId,
        select: (res) => res
    }
  );

  const records = data?.results || [];

  const handleEdit = (row) => {
    // এখানে সব ডাটা ডায়ালগে পাঠিয়ে দিচ্ছি যাতে এডিট কাজ করে
    setEditingConsent(row);
    setOpenDialog(true);
  };

  if (isLoading) return <Box textAlign="center" py={4}><CircularProgress size={24} /></Box>;

  if (records.length === 0) {
    return (
      <Box textAlign="center" py={3} sx={{ bgcolor: "#f8fafc", borderRadius: "8px", border: "1px dashed #e2e8f0" }}>
        <Typography variant="caption" color="textSecondary">No assessment history found for this reason.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ overflowX: "auto", border: "1px solid #f1f5f9", borderRadius: "12px" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8fafc" }}>
              <TableCell sx={{ fontWeight: 800, fontSize: "0.7rem", color: "#64748b" }}>DATE & TIME</TableCell>
              <TableCell sx={{ fontWeight: 800, fontSize: "0.7rem", color: "#64748b" }}>CONSENT STATUS</TableCell>
              <TableCell sx={{ fontWeight: 800, fontSize: "0.7rem", color: "#64748b" }}>METHOD / PROVIDER</TableCell>
              <TableCell sx={{ fontWeight: 800, fontSize: "0.7rem", color: "#64748b" }}>URGENCY</TableCell>
              <TableCell sx={{ fontWeight: 800, fontSize: "0.7rem", color: "#64748b" }}>RECORDED BY</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800, fontSize: "0.7rem", color: "#64748b" }}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((row) => (
              <TableRow key={row.id} hover sx={{ "& td": { py: 1.5 } }}>
                {/* 1. Date & Time */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    {new Date(row.consent_datetime || row.created_at).toLocaleDateString("en-GB")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(row.consent_datetime || row.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </TableCell>

                {/* 2. Consent Status & Signature Icon */}
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Chip 
                        label={row.given_consent ? "GRANTED" : "REFUSED"} 
                        size="small" 
                        sx={{ 
                            height: 20, fontSize: '0.65rem', fontWeight: 900, 
                            bgcolor: row.given_consent ? "#ecfdf5" : "#fef2f2", 
                            color: row.given_consent ? "#10b981" : "#ef4444" 
                        }} 
                    />
                    {row.signature && (
                      <Tooltip title="Digitally Signed">
                        <Verified sx={{ fontSize: 14, color: "#3b82f6" }} />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>

                {/* 3. Method & Provider */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                    {row.consent_not_signed_method || "Digital Signature"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Provider: {row.consent_provider || "Self"}
                  </Typography>
                </TableCell>

                {/* 4. Urgency */}
                <TableCell>
                  {row.decision_urgent ? (
                    <Chip label="URGENT" size="small" color="error" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 900 }} />
                  ) : (
                    <Typography variant="caption" color="textSecondary">Routine</Typography>
                  )}
                </TableCell>

                {/* 5. Recorded By */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>{row.created_by}</Typography>
                </TableCell>

                {/* 6. Actions */}
                <TableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <IconButton size="small" onClick={() => handleEdit(row)} sx={{ color: "#6366f1", bgcolor: "#f5f3ff" }}>
                      <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => onDelete(row.id)} sx={{ color: "#ef4444", bgcolor: "#fef2f2" }}>
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Dialog কল করার সময় প্রপস নাম এডিট মোডাল এর সাথে মিলিয়ে দেয়া হয়েছে */}
      <ConsentDialog
        open={openDialog}
        handleClose={() => { setOpenDialog(false); setEditingConsent(null); }}
        editingConsent={editingConsent} 
      />
    </>
  );
};

export default AssessmentTable;